import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMutating } from 'react-query';
import _ from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { isFactorMobile } from 'renative';

import {
    Asset,
    Broadcast,
    Favorite,
    isSeries,
    ASSET_TYPE,
    getIsPodcastSeries,
    getIsPodcastEpisode,
    PRODUCT_SCREENS,
    getIsNewsSeries,
    getAssetCanHaveSeasons,
    getEpisodesSorting,
    NEWS_SERIES_BACKSTAGE_LABEL,
} from '@24i/nxg-sdk-photon/src';
import { log } from '@24i/nxg-core-utils/src/logger';
import { useRemindersDataClient } from '@24i/nxg-sdk-smartott-shared/src/context/Reminders';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { ErrorCodes } from '@24i/nxg-sdk-smartott/src/utils/errorCodesMapper/types';
import prepareAutoPlay from '@24i/nxg-sdk-smartott/src/utils/prepareAutoPlay';
import useAssetActions from '@24i/nxg-sdk-smartott/src/hooks/useAssetActions';
import { showToast } from '@24i/nxg-sdk-gluons/src/components/ui/Toast';
import {
    useRecordingAssetQuery,
    useCreateRecordingForBroadcast,
    useDeleteRecordingForBroadcast,
    useCreateSeriesRecordingForBroadcast,
    useCancelRecordingsForSeries,
} from '@24i/nxg-sdk-smartott/src/hooks/query/recordings';
import {
    useAddToFavoritesMutation,
    useDeleteFromFavoritesMutation,
} from '@24i/nxg-sdk-smartott/src/hooks/query/favorites/useFavoritesMutation';
import useMyFavoritesQuery from '@24i/nxg-sdk-smartott/src/hooks/query/favorites/useMyFavoritesQuery';
import useContinueWatchingQuery from '@24i/nxg-sdk-smartott/src/hooks/query/continueWatching/useContinueWatchingQuery';
import { useAssetExtrasQuery } from '@24i/nxg-sdk-smartott/src/hooks/query/asset';
import {
    useAssetDetailsQuery,
    useAssetRelatedQuery,
    useAssetSeasonsQuery,
} from '@24i/nxg-sdk-smartott-shared/src/hooks/query/asset';
import { useTrackBuffering } from '@24i/nxg-sdk-smartott-shared/src/analytics/hooks/useTrackBuffering';

import { BlockModalTypes } from '../../../components/BlockedModal/types';
import { useStore } from '../../../context/ApplicationStore';
import { ScreenOrigin, ScreenOriginType } from '../../../navigation/types';
import { mapAssetToTrailer, mapUserSeasons } from './utils';
import {
    DetailsScreenProps,
    DETAILS_ERROR_ENUM,
    UseSharedModelDetails,
    ErrorToDiplay,
} from '../types';
import useAssetBlockersValidation from '../../../hooks/useAssetBlockersValidation';
import useBlockedModal from '../../../components/BlockedModal/hooks';
import { usePodcastPlayer } from '../../../context/PodcastPlayerProvider';

const useShared = (props: DetailsScreenProps): UseSharedModelDetails => {
    const { query = {}, recordError } = props;
    const contentDataClient = useContentData();

    let { asset: assetDetailsFromQuery } = query;

    const {
        origin,
        id = assetDetailsFromQuery?.id ?? '',
        type = assetDetailsFromQuery?.type ?? ASSET_TYPE.CUSTOM,
        channelId = assetDetailsFromQuery?.channelId ?? '',
        sectionLabel = assetDetailsFromQuery?.sectionLabel ?? '',
        attributionId = assetDetailsFromQuery?.attributionId,
        episodeToPlay,
    } = query;

    if (!assetDetailsFromQuery)
        assetDetailsFromQuery = {
            id,
            type,
            channelId,
            attributionId,
        };

    const { userData, selectedProfile } = useStore();
    const { data: continueWatchingPlaylist, refetch: refetchContinueWatching } =
        useContinueWatchingQuery();
    const { handleBlockersCheck } = useAssetBlockersValidation({});
    const { t } = useTranslation();
    const { openBlockedModal } = useBlockedModal();
    const {
        closePlayer: closePodcastPlayer,
        startLoading: startLoadingPodcastPlayer,
        stopLoading: stopLoadingPodcastPlayer,
        getIsEpisodeInPlayer: getIsPodcastEpisodeInPlayer,
    } = usePodcastPlayer();

    // Error handling

    const [recordingError, setRecordingError] = useState<string | undefined>(undefined);
    const [errorForModal, setErrorForModal] = useState<ErrorToDiplay | null>(null);

    const handleRecordingError = (error) => {
        const errorMessage = error instanceof Error ? error.message : t('error.A00.body');
        setRecordingError(errorMessage);
    };
    const handleDetailsError = (error, errorSection?: DETAILS_ERROR_ENUM) => {
        if (
            (error && error.error === ErrorCodes.ITEM_NOT_FOUND) ||
            error.message.includes(ErrorCodes.ITEM_NOT_FOUND)
        ) {
            setErrorForModal({ title: t('error.A01.title'), message: t('error.A01.body') });
        } else if (errorSection === DETAILS_ERROR_ENUM.RELATED) {
            // Ignore error for related assets, but track the error
        } else {
            setErrorForModal({ title: t('error.B00.title'), message: t('error.B00.body') });
        }
        log(`Error fetching asset details: ${errorSection}`, JSON.stringify(error));
        recordError?.(JSON.stringify(error));
    };

    // Query hooks

    const {
        data: loadedAsset,
        isLoading: isDetailsLoading,
        refetch: refetchDetails,
    } = useAssetDetailsQuery(
        { assetId: id, assetType: type, continueWatchingPlaylist, channelId, sectionLabel },
        {
            onError: (err) => handleDetailsError(err, DETAILS_ERROR_ENUM.DETAILS),
            ...(type === ASSET_TYPE.BROADCAST ? { staleTime: 0 } : null),
        }
    );
    const asset: Asset | Broadcast | undefined = useMemo(
        () =>
            loadedAsset
                ? {
                      ...loadedAsset,
                      attributionId,
                  }
                : undefined,
        [loadedAsset, attributionId]
    );
    useTrackBuffering({
        asset,
        screen: PRODUCT_SCREENS.DETAILS,
        origin,
    });

    const originFromHere = useMemo<ScreenOrigin>(
        () => ({
            type: type as unknown as ScreenOriginType,
            id,
            label: asset?.title ?? `Detail ${type} ${id}`,
        }),
        [asset]
    );

    const { startPlayback, getEpisodeToWatch, fetchEpisodeToReproduce } = useAssetActions({
        origin: originFromHere,
    });

    const { data: extras, isLoading: isExtrasLoading } = useAssetExtrasQuery(asset, {
        onError: (err) => handleDetailsError(err, DETAILS_ERROR_ENUM.EXTRAS),
        enabled: !isEmpty(asset) && type !== ASSET_TYPE.BROADCAST,
    });

    const { data: related, isLoading: isRelatedLoading } = useAssetRelatedQuery(
        { assetId: id, assetType: type },
        {
            onError: (err) => handleDetailsError(err, DETAILS_ERROR_ENUM.RELATED),
            enabled: !isEmpty(asset) && type !== ASSET_TYPE.BROADCAST,
        }
    );

    const assetCanHaveSeasons = getAssetCanHaveSeasons(type);

    const { data: seasons, isLoading: isSeasonsLoading } = useAssetSeasonsQuery(id, {
        onError: (err) => handleDetailsError(err, DETAILS_ERROR_ENUM.SEASONS),
        enabled: !_.isEmpty(asset) && assetCanHaveSeasons,
        select: (data) => mapUserSeasons(data, continueWatchingPlaylist, userData),
    });

    const orderedSeasons = useMemo(() => {
        if (!asset || !asset?.type) return [];
        if (!seasons) return [];
        const sortSeasons =
            asset?.type === ASSET_TYPE.PODCAST_SERIES
                ? (a, b) => b.seasonNumber - a.seasonNumber
                : (a, b) => a.seasonNumber - b.seasonNumber;
        const sortEpisodes = getEpisodesSorting(asset);
        return [...seasons].sort(sortSeasons).map((season) => {
            return {
                ...season,
                episodes:
                    season.episodes
                        ?.map((episode) => ({ ...episode, attributionId }))
                        ?.sort(sortEpisodes) || [],
            };
        });
    }, [seasons, asset?.type]);

    const mutationLoading = useIsMutating();
    const createRecordingForBroadcast = useCreateRecordingForBroadcast(handleRecordingError);
    const deleteRecordingForBroadcast = useDeleteRecordingForBroadcast({
        handleError: handleRecordingError,
        successToast: null,
    });
    const createSeriesRecordingForBroadcast =
        useCreateSeriesRecordingForBroadcast(handleRecordingError);
    const cancelRecordingsForSeries = useCancelRecordingsForSeries(handleRecordingError);
    const broadcastAsset = (asset || assetDetailsFromQuery) as Broadcast;
    const { data, isLoading: recordingAssetLoading } = useRecordingAssetQuery(broadcastAsset, {
        onError: (err) => handleRecordingError(err),
        enabled: !isEmpty(asset) && !!userData,
    });
    const {
        data: userFavorites,
        isFetching: favoritesFetching,
        isLoading: favoritesLoading,
    } = useMyFavoritesQuery({
        enabled: !isEmpty(asset),
    });
    const { mutate: deleteFromFavorites } = useDeleteFromFavoritesMutation();
    const { mutate: addToFavorites } = useAddToFavoritesMutation();

    const favoriteAsset = useMemo(
        () => userFavorites?.find((userFavorite: Favorite) => userFavorite.entityId === id),
        [userFavorites, id]
    );

    // States

    const [isAddedToMyList, setIsAddedToMyList] = useState(!!favoriteAsset);
    const [reminderIsSet, setReminderIsSet] = useState(false);
    const [fetchingReminder, setFetchingReminder] = useState(false);
    const toggleIsAddedToMyList = () => setIsAddedToMyList((prevState) => !prevState);
    const { remindersDataClient } = useRemindersDataClient();

    // Constants

    const isFavoritesLoading = favoritesFetching || favoritesLoading;

    const isLoading = isDetailsLoading || isExtrasLoading || isRelatedLoading || isSeasonsLoading;

    // Functions

    const onMyListPress = async (): Promise<void> => {
        try {
            if (userData) {
                if (!isFavoritesLoading) {
                    if (favoriteAsset) {
                        deleteFromFavorites(favoriteAsset.id);
                        setIsAddedToMyList(false);
                    } else if (type && id) {
                        const typeToSend =
                            type === ASSET_TYPE.NEWS_SERIES ? NEWS_SERIES_BACKSTAGE_LABEL : type;
                        addToFavorites({ entityType: typeToSend, entityId: id });
                        setIsAddedToMyList(true);
                    }
                }
            }
        } catch (err) {
            // If there was an error when adding or removing item, it toggles `My list` button value back
            toggleIsAddedToMyList();
            log('Error adding or removing item from user favorite list', err);
        }
    };

    const handlePurchaseAsset = useCallback(
        (selectedAsset: Asset, openPurchaseModal: (asset: Asset) => void) => {
            if (!userData) {
                openBlockedModal(BlockModalTypes.ACCESS);
                return;
            }

            let episode;
            if (selectedAsset.type === ASSET_TYPE.SERIES && !selectedAsset.isTrailer) {
                episode = getEpisodeToWatch(seasons || []);
            }

            openPurchaseModal(episode ?? selectedAsset);
        },
        [openBlockedModal, getEpisodeToWatch, seasons, userData]
    );

    const handleOnPlayPress = async ({
        streamAsset,
        isStartOver = false,
        onPurchaseAsset,
    }: {
        streamAsset: Asset;
        isStartOver?: boolean;
        onPurchaseAsset?: (assetToPurchase: Asset) => void;
    }) => {
        await prepareAutoPlay();

        const isNewsSeries = getIsNewsSeries(streamAsset);
        const isPodcastSeries = getIsPodcastSeries(streamAsset);
        const isPodcastEpisode = getIsPodcastEpisode(streamAsset);
        const shouldFollowPodcastFlow = isPodcastSeries || isPodcastEpisode;

        let assetToCheck: Asset | null | undefined = streamAsset;

        if (isPodcastSeries) {
            assetToCheck = await fetchEpisodeToReproduce(streamAsset, 'latest');
        }

        if (isNewsSeries) {
            assetToCheck = await contentDataClient.fetchLatestNewsEpisode(streamAsset.id);
        }

        if (streamAsset.type === ASSET_TYPE.SERIES && seasons) {
            assetToCheck = getEpisodeToWatch(seasons);
        }

        if (shouldFollowPodcastFlow) {
            if (assetToCheck && getIsPodcastEpisodeInPlayer(assetToCheck.id)) {
                return;
            }

            closePodcastPlayer();
            startLoadingPodcastPlayer();
        }

        const { hasBlocker } = await handleBlockersCheck({
            asset: assetToCheck ?? streamAsset,
            handlePurchase: () => onPurchaseAsset?.(assetToCheck ?? streamAsset),
        });

        if (hasBlocker) {
            if (shouldFollowPodcastFlow) closePodcastPlayer();
            return;
        }

        if (shouldFollowPodcastFlow) stopLoadingPodcastPlayer();

        startPlayback(streamAsset, isStartOver);
    };

    const logReminderError = (error: unknown): void => {
        if (error instanceof Error) {
            log('Error setting reminder', error);
        }
        showToast(t('error.B00.title'));
    };

    const fetchReminder = async (): Promise<void> => {
        if (selectedProfile && id) {
            setFetchingReminder(true);
            const { id: profileId } = selectedProfile;
            try {
                const reminder = await remindersDataClient.getReminderForBroadcast(id, profileId);
                if (reminder) {
                    setReminderIsSet(true);
                } else {
                    setReminderIsSet(false);
                }
                setFetchingReminder(false);
            } catch (error: unknown) {
                logReminderError(error);
            }
        }
    };

    const refetchData = () => {
        refetchDetails();
        if (userData) refetchContinueWatching();
    };

    const extraItems = useMemo(() => {
        const mappedExtras = extras?.map((extraAsset) => mapAssetToTrailer(extraAsset, t));
        if (
            isSeries(asset) &&
            mappedExtras &&
            seasons &&
            seasons.length > 0 &&
            seasons[0].name === 'Extra'
        ) {
            return [
                ...mappedExtras,
                // TODO: Temporary limit items to 3 because it causes performance issues on some mobile devices.
                // Figure out what would be a better solution (maybe `see all` feature)
                // P.S. this can be overridden via `renderPackshotGrid` prop if some projects wouldn't need to have this item limit
                ...seasons[0].episodes.filter((_i, index) => !isFactorMobile || index < 3),
            ];
        }
        return mappedExtras;
    }, [extras, asset, seasons]);

    // Use Effects

    useEffect(() => {
        if (!isFavoritesLoading) setIsAddedToMyList(!!favoriteAsset);
    }, [favoriteAsset, isFavoritesLoading]);

    const recordingState = useMemo(() => {
        return {
            status: data?.status,
            error: recordingError,
            loading: recordingAssetLoading || Boolean(mutationLoading),
            seriesId: data?.seriesId,
        };
    }, [data?.status, recordingError, recordingAssetLoading, mutationLoading, data?.seriesId]);
    return {
        isAddedToMyList,
        reminderIsSet,
        fetchingReminder,
        isLoading,
        originFromHere,
        asset,
        seasons: orderedSeasons,
        related,
        extraItems,
        errorForModal,
        recordingState,
        refetchData,
        fetchReminder,
        onMyListPress,
        setReminderIsSet,
        logReminderError,
        handleOnPlayPress,
        onSetSingleRecordingPress: () => createRecordingForBroadcast(broadcastAsset),
        onRemoveSingleRecordingPress: () => deleteRecordingForBroadcast(broadcastAsset),
        onSetMultipleRecordingPress: () => createSeriesRecordingForBroadcast(broadcastAsset),
        onRemoveMultipleRecordingPress: () => cancelRecordingsForSeries(broadcastAsset),
        handlePurchaseAsset,
        episodeToPlay,
    };
};

export { useShared };
